import type { Bearing, CockedHat, Mark } from "@nav-games/pilotage";

/// Visualise a three-point fix: marks at known positions, reciprocal
/// bearings drawn back through each, and the cocked-hat triangle at
/// the intersection.

interface Props {
  marks: readonly Mark[];
  bearings: readonly Bearing[];
  fix: CockedHat;
  size?: number;
}

const VW = 360;
const VH = 280;
const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";

function projector(
  marks: readonly Mark[],
  fix: CockedHat,
): { project: (lat: number, lon: number) => { x: number; y: number } } {
  // Combine marks + cocked-hat vertices for the bounding box.
  const lats = [
    ...marks.map((m) => m.latDeg),
    ...fix.vertices.map((v) => v.latDeg),
    fix.centroid.latDeg,
  ];
  const lons = [
    ...marks.map((m) => m.lonDeg),
    ...fix.vertices.map((v) => v.lonDeg),
    fix.centroid.lonDeg,
  ];
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  let minLon = Math.min(...lons);
  let maxLon = Math.max(...lons);

  // Pad with 15% so labels fit.
  const padLat = Math.max((maxLat - minLat) * 0.15, 0.005);
  const padLon = Math.max((maxLon - minLon) * 0.15, 0.005);
  minLat -= padLat;
  maxLat += padLat;
  minLon -= padLon;
  maxLon += padLon;

  const padPx = 24;
  const innerW = VW - 2 * padPx;
  const innerH = VH - 2 * padPx;
  const refLat = (minLat + maxLat) / 2;
  const cosRef = Math.cos((refLat * Math.PI) / 180);

  // Scale lat/lon so 1° lat = 1°/cosRef of lon visually (Mercator-ish).
  const lonRange = (maxLon - minLon) * cosRef;
  const latRange = maxLat - minLat;
  const sx = innerW / Math.max(lonRange, 0.0001);
  const sy = innerH / Math.max(latRange, 0.0001);
  const s = Math.min(sx, sy);

  // Centre the projection inside the viewbox.
  const xOffset = (innerW - lonRange * s) / 2;
  const yOffset = (innerH - latRange * s) / 2;

  return {
    project: (lat, lon) => ({
      x: padPx + xOffset + (lon - minLon) * cosRef * s,
      y: padPx + yOffset + (maxLat - lat) * s,
    }),
  };
}

export function ThreePointFixFigure({
  marks,
  bearings,
  fix,
  size = VW,
}: Props) {
  const { project } = projector(marks, fix);

  // Each position line drawn from the mark in the reciprocal of the
  // observed bearing — extended through the cocked hat.
  const lines = bearings.map((b) => {
    const start = project(b.mark.latDeg, b.mark.lonDeg);
    // Project an end point well past the centroid.
    const reciprocal = (b.trueDeg + 180) % 360;
    const rad = (reciprocal * Math.PI) / 180;
    const len = Math.max(VW, VH);
    return {
      start,
      end: {
        x: start.x + Math.sin(rad) * len,
        y: start.y - Math.cos(rad) * len,
      },
    };
  });

  const verts = fix.valid ? fix.vertices.map((v) => project(v.latDeg, v.lonDeg)) : [];
  const centroid = project(fix.centroid.latDeg, fix.centroid.lonDeg);

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Three-point fix — cocked hat diagram"
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      <rect
        x={0}
        y={0}
        width={VW}
        height={VH}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
        strokeWidth={1}
      />

      {/* Position lines (reciprocal of each bearing). */}
      {lines.map((l, i) => (
        <line
          key={i}
          x1={l.start.x}
          y1={l.start.y}
          x2={l.end.x}
          y2={l.end.y}
          stroke="#1e6091"
          strokeWidth={1.2}
        />
      ))}

      {/* Cocked-hat triangle. */}
      {fix.valid && verts.length === 3 && (
        <polygon
          points={verts.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="rgba(192, 56, 43, 0.15)"
          stroke="#c0382b"
          strokeWidth={1.5}
        />
      )}

      {/* Centroid — best-estimate position. */}
      {fix.valid && (
        <g>
          <circle
            cx={centroid.x}
            cy={centroid.y}
            r={5}
            fill="#c0382b"
            stroke="white"
            strokeWidth={1.5}
          />
          <text
            x={centroid.x + 8}
            y={centroid.y + 4}
            fontSize={10}
            fill="#c0382b"
            fontWeight={700}
          >
            Fix
          </text>
        </g>
      )}

      {/* Marks. */}
      {marks.map((m, i) => {
        const p = project(m.latDeg, m.lonDeg);
        return (
          <g key={`m-${i}`}>
            {/* Mark symbol — small triangle. */}
            <polygon
              points={`${p.x},${p.y - 6} ${p.x - 5},${p.y + 4} ${p.x + 5},${p.y + 4}`}
              fill={STROKE}
            />
            <text
              x={p.x + 8}
              y={p.y - 4}
              fontSize={10}
              fontWeight={600}
              fill={STROKE}
            >
              {m.label}
            </text>
            <text
              x={p.x + 8}
              y={p.y + 6}
              fontSize={9}
              fill={MUTED}
            >
              {bearings[i] ? `${bearings[i]!.trueDeg.toFixed(0)}°T` : ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
